/**
 * Technique Operations
 * 
 * Provides CRUD operations for techniques, user techniques, and positions
 * with proper offline handling, conflict detection, and sync queue management.
 */

import { BaseOperations } from './shared';
import { STORES } from '../schema';
import type { 
  TechniqueOffline, 
  UserTechniqueOffline, 
  PositionOffline 
} from '../schema';

/**
 * Techniques Operations
 */
export class TechniqueOperations extends BaseOperations<TechniqueOffline> {
  constructor() {
    super(STORES.TECHNIQUES);
  }

  /**
   * Get technique by ID (alias for get method)
   */
  async getById(id: string): Promise<TechniqueOffline | null> {
    return this.get(id);
  }

  /**
   * Get techniques by position
   */
  async getByPosition(positionId: string): Promise<TechniqueOffline[]> {
    return this.queryByIndex('by_position_id', positionId);
  }

  /**
   * Get techniques by type
   */
  async getByType(techniqueType: string): Promise<TechniqueOffline[]> {
    return this.queryByIndex('by_technique_type', techniqueType);
  }

  /**
   * Get techniques by difficulty
   */
  async getByDifficulty(difficulty: string): Promise<TechniqueOffline[]> {
    return this.queryByIndex('by_difficulty', difficulty);
  }

  /**
   * Get admin-created techniques
   */
  async getAdminTechniques(): Promise<TechniqueOffline[]> {
    return this.queryByIndex('by_is_admin_created', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get user-created techniques
   */
  async getUserCreatedTechniques(userId: string): Promise<TechniqueOffline[]> {
    return this.queryByIndex('by_created_by_user_id', userId);
  }

  /**
   * Get technique by slug
   */
  async getBySlug(slug: string): Promise<TechniqueOffline | null> {
    const allTechniques = await this.getAll();
    return allTechniques.find(technique => technique.slug === slug) || null;
  }

  /**
   * Search techniques by name (simple text search)
   */
  async searchByName(searchTerm: string): Promise<TechniqueOffline[]> {
    const allTechniques = await this.getAll();
    const lowerSearchTerm = searchTerm.toLowerCase();

    return allTechniques.filter(technique =>
      technique.name.toLowerCase().includes(lowerSearchTerm) ||
      technique.description.toLowerCase().includes(lowerSearchTerm)
    );
  }
}

/**
 * User Techniques Operations
 */
export class UserTechniqueOperations extends BaseOperations<UserTechniqueOffline> {
  constructor() {
    super(STORES.USER_TECHNIQUES);
  }

  /**
   * Get user techniques for a specific user
   */
  async getByUserId(userId: string): Promise<UserTechniqueOffline[]> {
    return this.queryByIndex('by_user_id', userId);
  }

  /**
   * Get favorite techniques for a user
   */
  async getFavorites(userId: string): Promise<UserTechniqueOffline[]> {
    const userTechniques = await this.getByUserId(userId);
    return userTechniques.filter(ut => ut.is_favorite === 1); // Filter by numeric value
  }

  /**
   * Check if a technique is saved by user
   */
  async isTechniqueSaved(userId: string, techniqueId: string): Promise<boolean> {
    const userTechniques = await this.queryByIndex('by_user_technique', [userId, techniqueId]);
    return userTechniques.length > 0;
  }

  /**
   * Get recently practiced techniques
   */
  async getRecentlyPracticed(userId: string, days = 30): Promise<UserTechniqueOffline[]> {
    const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const userTechniques = await this.getByUserId(userId);
    
    return userTechniques.filter(ut => 
      ut.last_practiced && ut.last_practiced >= cutoffDate
    );
  }
}

/**
 * Positions Operations
 */
export class PositionOperations extends BaseOperations<PositionOffline> {
  constructor() {
    super(STORES.POSITIONS);
  }

  /**
   * Get positions by category
   */
  async getByCategory(category: string): Promise<PositionOffline[]> {
    return this.queryByIndex('by_category', category);
  }

  /**
   * Search positions by name
   */
  async searchByName(searchTerm: string): Promise<PositionOffline[]> {
    const allPositions = await this.getAll();
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return allPositions.filter(position =>
      position.name.toLowerCase().includes(lowerSearchTerm)
    );
  }
}

// Export singleton instances
export const techniqueOps = new TechniqueOperations();
export const userTechniqueOps = new UserTechniqueOperations();
export const positionOps = new PositionOperations();
